import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface PopoverDisclosureOptions {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  getTriggerEl: () => HTMLElement | null;
  panelRef?: React.RefObject<HTMLElement>;
  closeOnOutsideClick?: boolean;
}
export interface PopoverDisclosure {
  isOpen: boolean;
  setOpen: (next: boolean, opts?: { skipFocusReturn?: boolean }) => void;
  toggle: () => void;
}

/** Controlled/uncontrolled popover open-state with outside-mousedown close, Esc-to-close
 *  (document keydown, defaultPrevented-aware so a panel can claim Esc first), and
 *  focus-return to the trigger. getTriggerEl is read via a ref so an inline-arrow caller
 *  never churns the listeners. panelRef is only dereferenced for outside-click. */
export function usePopoverDisclosure({
  open, onOpenChange, getTriggerEl, panelRef, closeOnOutsideClick = true,
}: PopoverDisclosureOptions): PopoverDisclosure {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open! : internalOpen;

  const getTriggerRef = useRef(getTriggerEl);
  getTriggerRef.current = getTriggerEl;

  const setOpen = useCallback((next: boolean, opts?: { skipFocusReturn?: boolean }) => {
    onOpenChange?.(next);
    if (!isControlled) setInternalOpen(next);
    if (!next && !opts?.skipFocusReturn) getTriggerRef.current()?.focus();
  }, [isControlled, onOpenChange]);

  const toggle = useCallback(() => setOpen(!isOpen), [isOpen, setOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef?.current?.contains(t)) return;
      const trig = getTriggerRef.current();
      if (trig && (trig === t || trig.contains(t))) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, closeOnOutsideClick, panelRef, setOpen]);

  return { isOpen, setOpen, toggle };
}
